import { Col, Form, InputNumber, Row, Switch, Tooltip } from 'antd';
import { RuleObject } from 'antd/es/form';
import { OrderPrestataireLine } from '.';
import { CatalogPriceZoneSPPriceFamily } from 'hooks/catalog-price-zone-service-provider';
import { Fragment } from 'react';

interface ProductQuantityCellProps {
  value?: {
    uuid?: string;
    productId?: number;
    productName?: string;
    priceFamilies?: CatalogPriceZoneSPPriceFamily[];
    selectedPriceFamilyId?: number | null;
    quantity?: number;
    isTransport?: boolean;
  }[];
  record: OrderPrestataireLine;
  updateDataSource?: (data: OrderPrestataireLine, serviceProviderId?: number) => void;
}

const ProductQuantityCell = (props: ProductQuantityCellProps) => {
  const { value, record, updateDataSource } = props;
  const form = Form.useFormInstance();

  const handleToggleChange = (checked: boolean, item: NonNullable<ProductQuantityCellProps['value']>[number]) => {
    const newQuantity = checked ? 1 : 0;
    const productKey = `${item?.uuid}-${item.productId}`;

    // Check if this is a transport product and if it's the only one with quantity = 1
    if (item.isTransport && !checked) {
      const activeTransportProducts = record.products.filter(
        (p) => p.isTransport && p.quantity === 1 && p.uuid !== item.uuid && p.productId !== item.productId,
      );
      if (activeTransportProducts.length === 0) {
        // Prevent turning off the only active transport product
        return;
      }
    }

    // Update form field for the toggled product
    form.setFieldsValue({
      lineItems: {
        [`${record.serviceProviderId}`]: {
          products: {
            [productKey]: {
              quantity: newQuantity,
            },
          },
        },
      },
    });

    // If the switch is turned on, set all other transport products to quantity 0
    if (checked && updateDataSource) {
      const updatedProducts = record.products.map((product) => {
        if (product.isTransport && product.uuid === item.uuid && product.productId === item.productId) {
          // Update the toggled product
          return { ...product, quantity: newQuantity };
        } else if (product.isTransport) {
          // Set other transport products to quantity 0
          return { ...product, quantity: 0 };
        }
        return product;
      });

      // Update form fields for other transport products
      updatedProducts.forEach((product) => {
        if (product.isTransport && (product.uuid !== item.uuid || product.productId !== item.productId)) {
          form.setFieldsValue({
            lineItems: {
              [`${record.serviceProviderId}`]: {
                products: {
                  [`${product.uuid}-${product.productId}`]: {
                    quantity: 0,
                  },
                },
              },
            },
          });
        }
      });

      // Update dataSource
      updateDataSource(
        {
          ...record,
          products: updatedProducts,
        },
        record.serviceProviderId,
      );
    } else if (updateDataSource) {
      // Update only the toggled product in dataSource
      const updatedProducts = record.products.map((product) =>
        product.uuid === item.uuid && product.productId === item.productId
          ? { ...product, quantity: newQuantity }
          : product,
      );

      updateDataSource(
        {
          ...record,
          products: updatedProducts,
        },
        record.serviceProviderId,
      );
    }
  };

  return (
    <section className='section'>
      {value?.map((item) => {
        const hasTarifSection = item?.priceFamilies && item.priceFamilies.length > 0;
        const isTransport = item?.isTransport;
        const isEnlevement = item?.productName && /Transport Benne : Enlèvement - .+/.test(item.productName);

        return (
          <div key={item?.uuid + '-' + item.productId}>
            <Row gutter={[16, 0]} align='middle' className='generate-price-value-cell'>
              <Col xs={{ span: 18 }} className='text-align-right' style={{ height: '32px' }}>
                <Form.Item
                  key={item.productId}
                  name={[
                    'lineItems',
                    `${record.serviceProviderId}`,
                    'products',
                    `${item?.uuid + '-' + item.productId}`,
                    'quantity',
                  ]}
                  rules={[
                    { required: true, message: 'Quantity is required' },
                    () => ({
                      validator(_: RuleObject, inputValue) {
                        if (isTransport) {
                          if (inputValue !== 0 && inputValue !== 1) {
                            return Promise.reject('Transport quantity must be 0 or 1');
                          }
                          // Removed the rule requiring at least one transport product to be enabled
                          // to allow all transport products to be off if needed
                        } else {
                          if (isNaN(inputValue) || parseFloat(inputValue) < 0) {
                            return Promise.reject('Invalid quantity');
                          }
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                  // initialValue={item.quantity}
                >
                  {isTransport ? (
                    isEnlevement ? null : (
                      <Tooltip
                          placement='bottom'
                          title={'Vous ne pouvez sélectionner qu’un seul produit transport'}
                          overlayInnerStyle={{ whiteSpace: 'pre-line' }}
                        >
                          <Switch
                            className='transport-switch'
                            checked={item.quantity === 1}
                            onChange={(checked) => handleToggleChange(checked, item)}
                            style={{ width: '50px', marginTop: ' 5px', marginLeft: '5px' }}
                            disabled={false}
                          />
                        </Tooltip>
                    )
                  ) : (
                    <InputNumber
                      disabled={true}
                      className='text-right quantity-cell'
                      style={{ width: '60px', textAlign: 'right' }}
                      formatter={(value) =>
                        value!
                          .toString()
                          .replace(/\s/g, '')
                          .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ')
                          .replace('.', ',')
                      }
                      controls={false}
                      parser={(value: string | undefined) => value!.replace(/\s/g, '').replace(/,/g, '.')}
                      onChange={(newValue) => {
                        if (updateDataSource) {
                          const updatedProducts = record.products.map((product) =>
                            product.uuid === item.uuid && product.productId === item.productId
                              ? { ...product, quantity: Number(newValue) }
                              : product
                          );
                          updateDataSource(
                            {
                              ...record,
                              products: updatedProducts,
                            },
                            record.serviceProviderId,
                          );
                        }
                        form.setFieldsValue({
                          lineItems: {
                            [`${record.serviceProviderId}`]: {
                              products: {
                                [`${item.uuid}-${item.productId}`]: {
                                  quantity: newValue,
                                },
                              },
                            },
                          },
                        });
                      }}
                    />
                  )}
                </Form.Item>
              </Col>
            </Row>
            {hasTarifSection && (
              <div className='generate-price-value-cell'>
                {generateTarifSectionSpace(item?.priceFamilies || [], item?.selectedPriceFamilyId)}
              </div>
            )}
          </div>
        );
      })}
    </section>
  );
};

const generateTarifSectionSpace = (priceFamilies: CatalogPriceZoneSPPriceFamily[], priceFamilyId?: number | null) => {
  let content: JSX.Element = <></>;
  const selectedPriceFamily = priceFamilies.find((priceFamily) => priceFamily.id === priceFamilyId);
  if (priceFamilies.length > 0) {
    content = (
      <>
        <Row gutter={[16, 0]} className='type-du-tarif'>
          <Col xs={{ span: 18 }} style={{ textAlign: 'right', paddingRight: 0 }}>
            <Form.Item initialValue={priceFamilyId}></Form.Item>
          </Col>
        </Row>
        {priceFamilyId && (
          <Row gutter={[16, 0]}>
            <Col xs={{ span: 24 }}>
              <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 8 }} className='long-label-item'></Form.Item>
            </Col>
          </Row>
        )}
        {selectedPriceFamily?.prices?.map((tarif, indexTarif) => (
          <Fragment key={`${tarif?.id}-${indexTarif}`}>
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }} id='info-tarif'></Col>
            </Row>
            {tarif?.priceOptions?.map((priceOption, indexPriceOption) => (
              <Fragment key={`${priceOption?.id}-${indexPriceOption}`}>
                <Row gutter={[16, 0]}>
                  <Col xs={{ span: 24 }}>
                    <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 8 }}></Form.Item>
                  </Col>
                </Row>
              </Fragment>
            ))}
            <Row gutter={[16, 0]}>
              <Col xs={{ span: 24 }}>
                <Form.Item labelCol={{ span: 24 }} wrapperCol={{ span: 8 }}></Form.Item>
              </Col>
            </Row>
          </Fragment>
        ))}
      </>
    );
  } else {
    content = (
      <>
        <Row gutter={[16, 0]}>
          <Col xs={{ span: 24 }} className='text-align-right'>
            <Form.Item></Form.Item>
          </Col>
        </Row>
      </>
    );
  }
  return <section className='section mt-2 mb-2 generate-tarif-section-empty-space'>{content}</section>;
};

export default ProductQuantityCell;
